import { useQuery } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { usePermissions } from '../../auth/use-permissions'
import { Menu } from '../../components/ui/menu'
import { currentLocale } from '../../i18n'
import { cn } from '../../lib/cn'
import type { SocketFactory } from '../../lib/socket'
import { formatAge, useNow } from '../map/relative-time'
import { fetchAlertCounts, fetchAlerts } from './api'
import type { AlertView } from './api-types'
import { AlertToasts } from './alert-toasts'
import { SeverityBadge } from './badges'
import { alertQueryKeys } from './queries'
import { ALERTS_PATH, alertCentrePath } from './routes'
import { useAlertStream } from './use-alert-stream'
import './i18n'

/**
 * The bell in the top bar, and the reason an alert is seen anywhere but /alerts.
 *
 * Before it, the live alert feed and its toasts were mounted on the alert centre and
 * nowhere else. A dispatcher watching the map — which is where a dispatcher is — was
 * never told that a vehicle had just been towed, crashed or started at 3 a.m.; the
 * alert existed, in a page nobody had open.
 *
 * WHAT THE NUMBER COUNTS. Open alerts, from the server — not "arrivals since this tab
 * was opened". The stream's own counter lives in memory and resets on every reload,
 * so a badge built on it would read 0 after F5 with nine unhandled alerts waiting. The
 * server count survives the reload, falls when someone acknowledges from another
 * screen, and the live stream only nudges it: each arrival invalidates the counts.
 *
 * ON THE ALERT CENTRE, THE BELL STEPS ASIDE. That page runs its own stream and its own
 * toasts; a second set here would show every arrival twice. The badge stays — it is a
 * count, not a notification.
 */

const MAX_BADGE = 99
const RECENT_COUNT = 8

export interface NotificationBellProps {
  /** Test seam: a fake socket factory. Production uses the real socket.io client. */
  socketFactory?: SocketFactory
}

export function NotificationBell({ socketFactory }: NotificationBellProps = {}) {
  const { t } = useTranslation()
  const { can } = usePermissions()
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const allowed = can('alerts:read')
  const onAlertCentre = pathname === ALERTS_PATH

  const counts = useQuery({
    queryKey: alertQueryKeys.counts,
    queryFn: fetchAlertCounts,
    enabled: allowed,
    // The stream invalidates on every arrival; this is the floor for when the socket is
    // down, and for acknowledgements made by somebody else.
    refetchInterval: 60_000,
  })

  const stream = useAlertStream({
    enabled: allowed && !onAlertCentre,
    ...(socketFactory === undefined ? {} : { socketFactory }),
  })

  if (!allowed) return null

  const open = counts.data?.open ?? 0
  const critical = (counts.data?.openCritical ?? 0) > 0
  const badge = open > MAX_BADGE ? `${MAX_BADGE}+` : String(open)
  const label = open === 0 ? t('alerts:bell.labelNone') : t('alerts:bell.label', { n: open })

  return (
    <>
      <Menu
        label={label}
        trigger={({ open: menuOpen }) => (
          <span
            className={cn(
              'relative inline-flex h-9 w-9 items-center justify-center rounded-md text-foreground-muted',
              'transition-colors hover:bg-surface-hover hover:text-foreground',
              menuOpen && 'bg-surface-hover text-foreground',
            )}
          >
            <Bell aria-hidden className="h-4.5 w-4.5" />
            {open > 0 ? (
              <span
                data-testid="bell-badge"
                className={cn(
                  'absolute -end-0.5 -top-0.5 min-w-[1.125rem] rounded-full px-1 text-center',
                  'text-[0.625rem] font-semibold leading-[1.125rem] text-white',
                  critical ? 'bg-danger' : 'bg-accent',
                )}
              >
                {badge}
              </span>
            ) : null}
          </span>
        )}
      >
        {(close) => (
          <BellPanel
            onOpenAlert={(alertId) => {
              close()
              navigate(alertCentrePath({ alertId }))
            }}
            onClose={close}
          />
        )}
      </Menu>

      {onAlertCentre ? null : (
        <AlertToasts
          alerts={stream.recent}
          onDismiss={stream.dismiss}
          onOpen={(alertId) => {
            stream.dismiss(alertId)
            navigate(alertCentrePath({ alertId }))
          }}
        />
      )}
    </>
  )
}

/** Fetched when the bell is opened, never on every arrival — see use-alert-stream.ts. */
function BellPanel({
  onOpenAlert,
  onClose,
}: {
  onOpenAlert: (alertId: string) => void
  onClose: () => void
}) {
  const { t } = useTranslation()
  const nowMs = useNow(30_000)
  const recent = useQuery({
    queryKey: [...alertQueryKeys.all, 'bell', RECENT_COUNT],
    queryFn: () => fetchAlerts({ pageSize: RECENT_COUNT }),
    staleTime: 0,
  })

  return (
    <div className="w-[22rem] max-w-[calc(100vw-2rem)]">
      <p className="px-2 pb-1.5 pt-1 text-xs font-medium uppercase tracking-wide text-foreground-subtle">
        {t('alerts:bell.title')}
      </p>

      {recent.isError ? (
        <p className="px-2 py-3 text-sm text-foreground-muted">{t('alerts:bell.error')}</p>
      ) : recent.data === undefined ? (
        <p className="px-2 py-3 text-sm text-foreground-muted">{t('alerts:bell.loading')}</p>
      ) : recent.data.items.length === 0 ? (
        <p className="px-2 py-3 text-sm text-foreground-muted">{t('alerts:bell.empty')}</p>
      ) : (
        <ul className="max-h-[24rem] overflow-y-auto">
          {recent.data.items.map((alert) => (
            <li key={alert.id}>
              <BellRow alert={alert} nowMs={nowMs} onOpen={() => onOpenAlert(alert.id)} />
            </li>
          ))}
        </ul>
      )}

      <div className="mt-1 border-t border-border pt-1">
        <Link
          to={ALERTS_PATH}
          onClick={onClose}
          className="block rounded px-2 py-1.5 text-center text-sm font-medium text-accent-text hover:bg-surface-hover"
        >
          {t('alerts:bell.seeAll')}
        </Link>
      </div>
    </div>
  )
}

function BellRow({
  alert,
  nowMs,
  onOpen,
}: {
  alert: AlertView
  nowMs: number
  onOpen: () => void
}) {
  const { t } = useTranslation()
  const occurredMs = Date.parse(alert.occurredAt)
  const age = Number.isNaN(occurredMs)
    ? ''
    : formatAge(currentLocale(), Math.max(0, (nowMs - occurredMs) / 1000))
  const handled = alert.status !== 'open'

  return (
    <button
      type="button"
      role="menuitem"
      onClick={onOpen}
      className={cn(
        'flex w-full items-start gap-2 rounded px-2 py-2 text-start transition-colors hover:bg-surface-hover',
        handled && 'opacity-60',
      )}
    >
      <span className="mt-0.5 shrink-0">
        <SeverityBadge severity={alert.severity} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">
          {t(`alerts:eventType.${alert.eventType}`)}
        </span>
        <span className="block truncate text-xs text-foreground-muted">
          {[alert.vehicleLabel, alert.address].filter(Boolean).join(' · ') ||
            t('alerts:bell.fleetWide')}
        </span>
      </span>
      <span className="shrink-0 text-xs text-foreground-subtle">{age}</span>
    </button>
  )
}
